import cn from 'classnames'
import DropdownMenu from 'ext-ui/components/Dropdown/components/DropdownMenu/DropdownMenu'
import {
  DropdownValue,
  useDropdownLogic,
} from 'ext-ui/components/Dropdown/hooks/useDropdownLogic'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import { useTranslate } from 'src/localization/Trns'
import { assertNever } from 'src/queries/agent/network/agent.network'
import { Vendor } from 'src/queries/vendors/vendors.types'

import { requiredAsterisk } from '../../billPayFlyoutChrome.style'
import { PlusIcon } from './icons'
import {
  useVendorSelectOptions,
  VendorOptionDetails,
} from './useVendorSelectOptions'
import styles, {
  emptyState,
  emptyTitle,
  footerAction,
  loadingOption,
  noMatch,
} from './VendorSelect.style'
import VendorSelectHandle from './VendorSelectHandle'
import VendorSelectOption from './VendorSelectOption'

/**
 * Links to the Vendors page, which owns the real Add-Vendor flyout. A plain
 * anchor because a feature component must not import routing APIs (the
 * page-owns-routing rule).
 *
 * No `?addVendor=1`: VendorsPage drives its flyout from local state and never
 * reads that param, so it was an inert promise of a deep link. The app's real
 * convention for opening an overlay from a URL is the reserved `?flyout=<name>`
 * namespace (see src/navigation/AGENTS.md) — worth wiring up properly rather
 * than inventing a param nothing consumes.
 */
const VENDORS_HREF = '/vendors'

const VENDOR_LABEL_ID = 'pay-bill-vendor-label'
const VENDOR_HANDLE_ID = 'pay-bill-vendor-handle'

export interface VendorSelectProps {
  /** The full vendor list; the payable filter is applied internally. */
  vendors: Vendor[]
  isLoading?: boolean
  selectedVendor: Vendor | null
  onSelect: (vendor: Vendor) => void
  className?: string
}

/**
 * Vendor picker for the Pay Bill flyout. Composes the app's dropdown machinery
 * (`useDropdownLogic` + `DropdownMenu`) with a custom two-line trigger and row —
 * modelled directly on `CreditCardDropdownV2` — because the vendor identity
 * block, the avatar rows, and the link-bearing empty state are all things the
 * plain `SingleDropdown` cannot render.
 */
const VendorSelect: React.FC<VendorSelectProps> = ({
  vendors,
  isLoading = false,
  selectedVendor,
  onSelect,
  className,
}) => {
  const translate = useTranslate()

  const {
    optionGroups,
    infiniteScrollProps,
    hasPayableVendors,
    searchQuery,
    resetSearch,
  } = useVendorSelectOptions(vendors, isLoading)

  const selectedValues = useMemo<DropdownValue<VendorOptionDetails>[]>(
    () =>
      selectedVendor
        ? [
            {
              value: selectedVendor.id,
              label: selectedVendor.name,
              details: { vendor: selectedVendor },
            },
          ]
        : [],
    [selectedVendor],
  )

  const handleSelect = useCallback(
    (value: DropdownValue<VendorOptionDetails>): void => {
      const vendor = value.details?.vendor
      if (vendor) onSelect(vendor)
    },
    [onSelect],
  )

  const {
    rows,
    open,
    getMenuProps,
    getItemProps,
    getDropdownHandleProps,
    getSearchInputProps,
    containerRef,
    optimisticSelectedValues,
    clearSearch,
  } = useDropdownLogic<VendorOptionDetails>({
    groups: optionGroups,
    selectedValues,
    infiniteScrollProps,
    onSelect: handleSelect,
    searchable: true,
    // Vendor is required — there is no "clear selection" (none) row.
    noneLabel: '',
  })

  // Reset the search each time the panel closes, so it reopens blank (mirrors
  // CreditCardDropdownV2). `clearSearch` resets the hook's own controlled search
  // input; `resetSearch` resets the list-filtering query behind it.
  const prevOpenRef = useRef(false)
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      clearSearch()
      resetSearch()
    }
    prevOpenRef.current = open
  }, [open, clearSearch, resetSearch])

  const handleDivRef = useRef<HTMLDivElement>(null)
  const { ref: _handleRef, ...handleProps } = getDropdownHandleProps()
  const { anchorEl: _anchorEl, ...menuProps } = getMenuProps()

  const selectedIds = optimisticSelectedValues.map((value) => value.value)

  return (
    <div
      className={cn('VendorSelect', className)}
      css={styles}
      ref={containerRef}
    >
      <FieldLabel>
        <span id={VENDOR_LABEL_ID}>{translate('Vendor')}</span>
        <span css={requiredAsterisk} aria-hidden={'true'}>
          {'*'}
        </span>
      </FieldLabel>
      <VendorSelectHandle
        ref={handleDivRef}
        id={VENDOR_HANDLE_ID}
        aria-labelledby={VENDOR_LABEL_ID}
        open={open}
        selectedVendor={selectedVendor}
        placeholder={translate('Search or select a vendor')}
        {...handleProps}
      />
      <DropdownMenu
        anchorEl={handleDivRef.current}
        {...menuProps}
        open={open}
        searchProps={getSearchInputProps()}
        searchable
        infiniteScrollProps={infiniteScrollProps}
        menuTestId={'vendor-select-menu'}
        /* Pinned below the list rather than being its last row, so it is
           visible without scrolling however many vendors there are, and
           right-aligned — matching the prototype and the design system's
           dropdown-menu-footer. Hidden on the truly-empty state, which shows
           its own "Add Vendor" action in the panel body instead. */
        footer={
          hasPayableVendors ? (
            <a css={footerAction} href={VENDORS_HREF}>
              <PlusIcon />
              {translate('Add new vendor')}
            </a>
          ) : undefined
        }
      >
        {rows.map((row, index) => {
          switch (row.kind) {
            case 'heading':
              return null
            case 'loading':
              return (
                <div css={loadingOption} key={'loading'}>
                  {translate('Loading...')}
                </div>
              )
            case 'empty':
              return row.hasSearchQuery ? (
                <div css={noMatch} key={'empty'} role={'status'}>
                  {translate('No vendors match "${query}"', {
                    query: searchQuery,
                  })}
                </div>
              ) : (
                <div css={emptyState} key={'empty'}>
                  <span css={emptyTitle}>
                    {translate('No vendors with a payment method on file yet.')}
                  </span>
                  <a css={footerAction} href={VENDORS_HREF}>
                    <PlusIcon />
                    {translate('Add Vendor')}
                  </a>
                </div>
              )
            case 'option': {
              const opt = row.option
              return (
                <VendorSelectOption
                  key={opt.value}
                  name={opt.label ?? ''}
                  email={opt.sublabel}
                  selected={selectedIds.includes(opt.value)}
                  data-testid={`vendor-option-${opt.value}`}
                  {...getItemProps({ item: row, index })}
                />
              )
            }
            default:
              return assertNever(row)
          }
        })}
      </DropdownMenu>
    </div>
  )
}

export default VendorSelect
